import React, { Fragment, type FC, type SVGProps } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import type { FormErrors } from '@jwp/ott-common/types/form';
import type { Offer, ChooseOfferFormData } from '@jwp/ott-common/types/checkout';
import { getOfferPrice } from '@jwp/ott-common/src/utils/offers';
import { testId } from '@jwp/ott-common/src/utils/common';
import CheckCircle from '@jwp/ott-theme/assets/icons/check_circle.svg?react';

import Button from '../Button/Button';
import FormFeedback from '../FormFeedback/FormFeedback';
import DialogBackButton from '../DialogBackButton/DialogBackButton';
import LoadingOverlay from '../LoadingOverlay/LoadingOverlay';
import Icon from '../Icon/Icon';
import type { OfferTabs } from '../../containers/AccountModal/forms/ChooseOffer';

import styles from './ChooseOfferForm.module.scss';

type OfferBoxProps = {
  offer: Offer;
  selected: boolean;
  showOfferTitle: boolean;
} & Pick<Props, 'onChange'>;

const getTranslatedProperty = (source: Record<string, unknown>, propertyKey: string, language: string | undefined): string => {
  const [code, region] = language?.split('-') || [];
  const translatedPropertyKeys = region ? [`${propertyKey}-${language}`, `${propertyKey}-${code}`] : [`${propertyKey}-${language}`];

  for (const key of translatedPropertyKeys) {
    if (key in source && typeof source[key] === 'string' && source[key] !== '') {
      return source[key];
    }
  }

  if (typeof source[propertyKey] === 'string') {
    return source[propertyKey];
  }

  return '';
};

const OfferBox: React.FC<OfferBoxProps> = ({ offer, selected, onChange, showOfferTitle }: OfferBoxProps) => {
  const { t, i18n } = useTranslation('account');

  const getFreeTrialText = (offer: Offer) => {
    if (offer.freeDays) {
      return t('choose_offer.benefits.first_days_free', { count: offer.freeDays });
    } else if (offer.freePeriods) {
      // t('periods.day', { count })
      // t('periods.week', { count })
      // t('periods.month', { count })
      // t('periods.year', { count })
      const period = t(`periods.${offer.period}`, { count: offer.freePeriods });

      return t('choose_offer.benefits.first_periods_free', { count: offer.freePeriods, period });
    }

    return null;
  };

  const renderListItem = (text: string, icon: FC<SVGProps<SVGSVGElement>>) => (
    <li key={text}>
      <Icon icon={icon} />
      {text}
      <span className="hidden">.</span>
    </li>
  );

  const renderOption = ({ title, periodString, features }: { title: string; periodString?: string; features: string[] }) => (
    <div className={styles.offer} aria-labelledby={`title-${offer.offerId}`}>
      <input
        className={styles.radio}
        onChange={onChange}
        type="radio"
        name={'selectedOfferId'}
        value={offer.offerId}
        id={offer.offerId}
        checked={selected}
        data-testid={testId(offer.offerId)}
      />
      <div className={styles.label}>
        <label htmlFor={offer.offerId}>
          <h2 className={styles.offerTitle} id={`title-${offer.offerId}`}>
            {title}
          </h2>
          <span className="hidden">.</span>
          <hr className={styles.offerDivider} aria-hidden={true} />
          <ul className={styles.offerBenefits}>
            {offer.freeDays || offer.freePeriods ? renderListItem(getFreeTrialText(offer) || '', CheckCircle) : null}
            {features.map((feature) => renderListItem(feature, CheckCircle))}
          </ul>
          <div className={styles.fill} />
          <div className={styles.offerPrice}>
            {getOfferPrice(offer)} {!!periodString && <small>/{periodString}</small>}
          </div>
        </label>
      </div>
    </div>
  );

  const customFeaturesProperty = getTranslatedProperty(offer, 'features', i18n.language);
  const customFeatures = customFeaturesProperty
    ? customFeaturesProperty
        .split(',')
        .map((feature) => feature.trim())
        .filter(Boolean)
    : [];

  if (offer.type === 'subscription') {
    const isMonthly = offer.period === 'month';
    let title = isMonthly ? t('choose_offer.monthly') : t('choose_offer.yearly');

    if (showOfferTitle) title = `${offer.offerTitle} - ${title}`;

    const features = customFeatures.length ? customFeatures : [t('choose_offer.benefits.cancel_anytime'), t('choose_offer.benefits.watch_on_all_devices')];

    return renderOption({
      title,
      features,
      periodString: isMonthly ? t('periods.month') : t('periods.year'),
    });
  }

  const tvodAccessFeature =
    !!offer.durationPeriod && !!offer.durationAmount ? t('choose_offer.tvod_access', { period: offer.durationPeriod, count: offer.durationAmount }) : '';

  const tvodFeatures = customFeatures.length ? customFeatures : [t('choose_offer.benefits.watch_on_all_devices')];

  return renderOption({
    title: offer.offerTitle,
    features: [tvodAccessFeature, ...tvodFeatures].filter(Boolean),
  });
};

type Props = {
  values: ChooseOfferFormData;
  errors: FormErrors<ChooseOfferFormData>;
  onChange: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  onSubmit: React.FormEventHandler<HTMLFormElement>;
  onBackButtonClickHandler?: () => void;
  offerTabs: OfferTabs;
  submitting: boolean;
};

const ChooseOfferForm: React.FC<Props> = ({ values, errors, submitting, offerTabs, onChange, onSubmit, onBackButtonClickHandler }: Props) => {
  const { t } = useTranslation('account');
  const { selectedOfferId, selectedOfferTab } = values;
  const offers = (selectedOfferTab !== undefined && offerTabs[selectedOfferTab]?.offers) || [];
  const showOfferTabs = Object.values(offerTabs).length > 1;

  return (
    <form onSubmit={onSubmit} data-testid={testId('choose-offer-form')} noValidate>
      {onBackButtonClickHandler ? <DialogBackButton onClick={onBackButtonClickHandler} /> : null}
      <h1 className={styles.title}>{t('choose_offer.title')}</h1>
      {errors.form ? <FormFeedback variant="error">{errors.form}</FormFeedback> : null}
      {showOfferTabs && (
        <div className={styles.offerGroupSwitch}>
          {offerTabs.map(({ offerGroupId: id, label }, index) => (
            <Fragment key={id}>
              <input
                className={styles.radio}
                onChange={onChange}
                type="radio"
                name="selectedOfferTab"
                id={id}
                value={index}
                checked={index === Number(selectedOfferTab)}
              />
              <label className={classNames(styles.label, styles.offerGroupLabel)} htmlFor={id}>
                {label}
              </label>
            </Fragment>
          ))}
        </div>
      )}
      <div className={styles.offers}>
        {!offers.length ? (
          <p>{t('choose_offer.no_pricing_available')}</p>
        ) : (
          offers.map((offer) => (
            <OfferBox
              key={offer.offerId}
              offer={offer}
              selected={selectedOfferId === offer.offerId}
              onChange={onChange}
              showOfferTitle={!showOfferTabs && offers.length === 1}
            />
          ))
        )}
      </div>
      {submitting && <LoadingOverlay transparentBackground inline />}
      <Button label={t('choose_offer.continue')} disabled={submitting || !offers.length} variant="contained" color="primary" type="submit" fullWidth />
    </form>
  );
};
export default ChooseOfferForm;
